/**
 * Launch catalog — 22 SKUs supplied directly by the operator via WhatsApp
 * on 2026-06-02. Category pricing (operator-set):
 *
 *   - Jordan       — $129.99   (all silhouettes, all colorways)
 *   - Air Force 1  —  $59.99
 *   - New Balance  —  $69.99
 *   - Slides       —  $79.99   (confirmed with operator 2026-06-05)
 *
 * Shipping is flat $9.99 ("Envío $9.99") across all orders, see SHIPPING.
 *
 * Pricing stored as integer cents to avoid float drift. Display formatter
 * is `formatPrice`. SKU convention: short kebab slug per model + colorway.
 *
 * Superseded as the live source of truth on 2026-08 by `lib/catalog.ts`,
 * which fetches the real catalog from the PAG Commerce backend (see
 * lib/pag-commerce/). This array is now only the STATIC launch snapshot —
 * kept as the shape reference for the `Product` type and as a historical
 * record; it is no longer read by the shop/PDP/homepage at request time.
 */

export type Product = {
  id: string;
  brand: string;
  model: string;
  colorway: string;
  /** Selling price, in integer cents (e.g. 12999 = $129.99). */
  priceCents: number;
  sku: string;
  /** US sizes carried. Empty array = sold out. */
  sizesAvailable: number[];
  /** Optional /public path or PAG Commerce image URL for hero/grid imagery. */
  imageSrc?: string;
  /** PAG Commerce product UUID — set on dynamically-fetched products (for the cart). */
  commerceProductId?: string;
  /** size -> PAG Commerce variant UUID — set on dynamic products (for checkout). */
  variantIdBySize?: Record<string, string>;
  /** Full image gallery (hero first), from PAG Commerce. Falls back to productGallery(). */
  gallery?: string[];
};

/**
 * Product ids that carry additional angle shots at /products/<id>-2.jpg,
 * -3.jpg, -4.jpg (front 3/4, opposite side, back 3/4). The 3 collab pairs
 * with no available 360 set keep a single hero image.
 */
export const IDS_WITH_ANGLES = new Set<string>([
  "af1-07-black", "af1-07-white", "aj1-low-fragment-ts-sail-military-blue",
  "aj1-low-ts-sail-tropical-pink", "aj1-low-ts-shy-pink", "aj11-gamma-blue-2025",
  "aj11-legend-blue-2024", "aj12-bloodline", "aj4-black-cat-2025", "aj4-lakers",
  "aj4-toro-bravo-2026", "aj4-white-cement-2025", "aj5-wolf-grey-2026",
  "mind001-black-chrome", "mind001-light-bone", "mind001-light-smoke-grey",
  "mind001-solar-red", "nb-9060-triple-black",
  // ── Supplier drop 2026-06-18 — products with a StockX 360 set ──
  "aj3-pure-money-2025", "aj3-white-cement-reimagined", "aj3-black-cat-2025",
  "aj3-seoul-20", "aj3-sp-j-balvin", "aj4-white-thunder", "aj4-og-sp-undefeated-2025",
  "aj5-black-university-blue-2026", "aj5-fire-red-black-tongue-2025",
  "aj5-white-metallic-2026", "aj5-tokyo-t23-2025", "aj5-og-black-metallic-reimagined",
  "aj5-medium-soft-pink", "aj6-reverse-infrared", "aj11-playoffs-2019",
  "aj11-cool-grey-2021", "aj11-cherry-2022", "aj12-taxi-2025", "aj12-flu-game-2025",
  "aj12-french-blue-2025", "aj12-blueberry", "aj13-flint-2020",
]);

/** Full image gallery for a product: hero first, then angle shots if present. */
export function productGallery(p: Product): string[] {
  if (!p.imageSrc) return [];
  if (!IDS_WITH_ANGLES.has(p.id)) return [p.imageSrc];
  const base = p.imageSrc.replace(/\.jpg$/i, "");
  return [p.imageSrc, `${base}-2.jpg`, `${base}-3.jpg`, `${base}-4.jpg`];
}

// ─── Pricing tiers (operator-set, 2026-06-02) ─────────────────────────
const JORDAN_PRICE = 12999;
const AF1_PRICE = 5999;
const NB_PRICE = 6999;
const SLIDE_PRICE = 7999; // confirmed with operator 2026-06-05

// ─── Standard size run (defaults for Jordan + Nike + New Balance) ─────
const STANDARD_SIZES = [8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12];
const SLIDE_SIZES = [8, 9, 10, 11, 12, 13];
// Supplier drop 2026-06-18 (Jonatan via WhatsApp): "del 7 al 12 todos".
const SUPPLIER_SIZES_7_12 = [7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12];

export const PRODUCTS: Product[] = [
  // ─── Jordan 1 — Travis Scott Low SP series ────────────────────────────
  {
    id: "aj1-low-ts-medium-olive",
    brand: "Jordan",
    model: "Air Jordan 1 Retro Low OG SP Travis Scott",
    colorway: "Medium Olive",
    priceCents: JORDAN_PRICE,
    sku: "AJ1L-TS-OLIVE",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj1-low-ts-medium-olive.jpg",
  },
  {
    id: "aj1-low-ts-sail-tropical-pink",
    brand: "Jordan",
    model: "Air Jordan 1 Retro Low OG SP Travis Scott",
    colorway: "Sail / Tropical Pink",
    priceCents: JORDAN_PRICE,
    sku: "AJ1L-TS-TROPPINK",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj1-low-ts-sail-tropical-pink.jpg",
  },
  {
    id: "aj1-low-ts-shy-pink",
    brand: "Jordan",
    model: "Air Jordan 1 Retro Low OG SP Travis Scott",
    colorway: "Shy Pink",
    priceCents: JORDAN_PRICE,
    sku: "AJ1L-TS-SHYPINK",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj1-low-ts-shy-pink.jpg",
  },
  {
    id: "aj1-low-fragment-ts-sail-military-blue",
    brand: "Jordan",
    model: "Air Jordan 1 Retro Low OG SP Fragment x Travis Scott",
    colorway: "Sail / Military Blue",
    priceCents: JORDAN_PRICE,
    sku: "AJ1L-FRAG-TS-MIL",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj1-low-fragment-ts-sail-military-blue.jpg",
  },

  // ─── Jordan 1 — Virgil Abloh ──────────────────────────────────────────
  {
    id: "aj1-high-virgil-archive-alaska",
    brand: "Jordan",
    model: "Air Jordan 1 Retro High Virgil Abloh",
    colorway: "Archive / Alaska",
    priceCents: JORDAN_PRICE,
    sku: "AJ1H-VA-ALASKA",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj1-high-virgil-archive-alaska.jpg",
  },

  // ─── Jordan 4 ─────────────────────────────────────────────────────────
  {
    id: "aj4-white-cement-2025",
    brand: "Jordan",
    model: "Air Jordan 4 Retro",
    colorway: "White Cement (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-WC25",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj4-white-cement-2025.jpg",
  },
  {
    id: "aj4-toro-bravo-2026",
    brand: "Jordan",
    model: "Air Jordan 4 Retro",
    colorway: "Toro Bravo (2026)",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-TORO26",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj4-toro-bravo-2026.jpg",
  },
  {
    id: "aj4-nigel-brick-after-brick",
    brand: "Jordan",
    model: "Air Jordan 4 Retro OG Nigel Sylvester",
    colorway: "Brick After Brick",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-NIGEL-BRICK",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj4-nigel-brick-after-brick.jpg",
  },
  {
    id: "aj4-black-cat-2025",
    brand: "Jordan",
    model: "Air Jordan 4 Retro",
    colorway: "Black Cat (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-BC25",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj4-black-cat-2025.jpg",
  },
  {
    id: "aj4-lakers",
    brand: "Jordan",
    model: "Air Jordan 4 Retro",
    colorway: "Lakers",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-LAKERS",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj4-lakers.jpg",
  },

  // ─── Jordan 5 ─────────────────────────────────────────────────────────
  {
    id: "aj5-wolf-grey-2026",
    brand: "Jordan",
    model: "Air Jordan 5 Retro",
    colorway: "Wolf Grey (2026)",
    priceCents: JORDAN_PRICE,
    sku: "AJ5-WG26",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj5-wolf-grey-2026.jpg",
  },

  // ─── Jordan 11 ────────────────────────────────────────────────────────
  {
    id: "aj11-gamma-blue-2025",
    brand: "Jordan",
    model: "Air Jordan 11 Retro",
    colorway: "Gamma Blue (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ11-GAMMA25",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj11-gamma-blue-2025.jpg",
  },
  {
    id: "aj11-legend-blue-2024",
    brand: "Jordan",
    model: "Air Jordan 11 Retro",
    colorway: "Legend Blue (2024)",
    priceCents: JORDAN_PRICE,
    sku: "AJ11-LEG24",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj11-legend-blue-2024.jpg",
  },

  // ─── Jordan 12 ────────────────────────────────────────────────────────
  {
    id: "aj12-bloodline",
    brand: "Jordan",
    model: "Air Jordan 12 Retro",
    colorway: "Bloodline",
    priceCents: JORDAN_PRICE,
    sku: "AJ12-BLOOD",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/aj12-bloodline.jpg",
  },

  // ─── Nike Mind 001 Slides ─────────────────────────────────────────────
  {
    id: "mind001-black-chrome",
    brand: "Nike",
    model: "Nike Mind 001 Slide",
    colorway: "Black Chrome",
    priceCents: SLIDE_PRICE,
    sku: "M001-BC",
    sizesAvailable: SLIDE_SIZES,
    imageSrc: "/products/mind001-black-chrome.jpg",
  },
  {
    id: "mind001-light-smoke-grey",
    brand: "Nike",
    model: "Nike Mind 001 Slide",
    colorway: "Light Smoke Grey",
    priceCents: SLIDE_PRICE,
    sku: "M001-LSG",
    sizesAvailable: SLIDE_SIZES,
    imageSrc: "/products/mind001-light-smoke-grey.jpg",
  },
  {
    id: "mind001-solar-red",
    brand: "Nike",
    model: "Nike Mind 001 Slide",
    colorway: "Solar Red",
    priceCents: SLIDE_PRICE,
    sku: "M001-SR",
    sizesAvailable: SLIDE_SIZES,
    imageSrc: "/products/mind001-solar-red.jpg",
  },
  {
    id: "mind001-light-bone",
    brand: "Nike",
    model: "Nike Mind 001 Slide",
    colorway: "Light Bone",
    priceCents: SLIDE_PRICE,
    sku: "M001-LB",
    sizesAvailable: SLIDE_SIZES,
    imageSrc: "/products/mind001-light-bone.jpg",
  },

  // ─── Air Force 1 ──────────────────────────────────────────────────────
  {
    id: "af1-07-white",
    brand: "Nike",
    model: "Air Force 1 Low '07",
    colorway: "Triple White",
    priceCents: AF1_PRICE,
    sku: "AF1-07-WHITE",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/af1-07-white.jpg",
  },
  {
    id: "af1-07-black",
    brand: "Nike",
    model: "Air Force 1 Low '07",
    colorway: "Triple Black",
    priceCents: AF1_PRICE,
    sku: "AF1-07-BLACK",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/af1-07-black.jpg",
  },

  // ─── New Balance ──────────────────────────────────────────────────────
  {
    id: "nb-9060-triple-black",
    brand: "New Balance",
    model: "9060",
    colorway: "Triple Black",
    priceCents: NB_PRICE,
    sku: "NB9060-BLACK",
    sizesAvailable: STANDARD_SIZES,
    imageSrc: "/products/nb-9060-triple-black.jpg",
  },

  // ════════════════════════════════════════════════════════════════════
  // Supplier drop — Jonatan via WhatsApp 2026-06-18. All Jordan ($129.99),
  // sizes 7–12 incl. halves. StockX slugs drive image fetch.
  // ════════════════════════════════════════════════════════════════════

  // ─── Jordan 3 ─────────────────────────────────────────────────────────
  {
    id: "aj3-black-cement-2024",
    brand: "Jordan",
    model: "Air Jordan 3 Retro",
    colorway: "Black Cement (2024)",
    priceCents: JORDAN_PRICE,
    sku: "AJ3-BLKCEM24",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj3-black-cement-2024.jpg",
  },
  {
    id: "aj3-pure-money-2025",
    brand: "Jordan",
    model: "Air Jordan 3 Retro",
    colorway: "Pure Money (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ3-PUREMON25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj3-pure-money-2025.jpg",
  },
  {
    id: "aj3-white-cement-reimagined",
    brand: "Jordan",
    model: "Air Jordan 3 Retro",
    colorway: "White Cement Reimagined",
    priceCents: JORDAN_PRICE,
    sku: "AJ3-WHTCEMRE",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj3-white-cement-reimagined.jpg",
  },
  {
    id: "aj3-black-cat-2025",
    brand: "Jordan",
    model: "Air Jordan 3 Retro",
    colorway: "Black Cat (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ3-BLKCAT25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj3-black-cat-2025.jpg",
  },
  {
    id: "aj3-j-balvin-rio",
    brand: "Jordan",
    model: "Air Jordan 3 Retro SP J Balvin",
    colorway: "Rio",
    priceCents: JORDAN_PRICE,
    sku: "AJ3-JBALVRIO",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj3-j-balvin-rio.jpg",
  },
  {
    id: "aj3-seoul-20",
    brand: "Jordan",
    model: "Air Jordan 3 Retro",
    colorway: "Seoul 2.0",
    priceCents: JORDAN_PRICE,
    sku: "AJ3-SEOUL20",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj3-seoul-20.jpg",
  },
  {
    id: "aj3-sp-j-balvin",
    brand: "Jordan",
    model: "Air Jordan 3 Retro SP J Balvin",
    colorway: "OG",
    priceCents: JORDAN_PRICE,
    sku: "AJ3-SPJBALV",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj3-sp-j-balvin.jpg",
  },

  // ─── Jordan 4 (supplier drop) ─────────────────────────────────────────
  {
    id: "aj4-rare-air-white-lettering",
    brand: "Jordan",
    model: "Air Jordan 4 Retro",
    colorway: "Rare Air White Lettering",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-RAREAIR",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj4-rare-air-white-lettering.jpg",
  },
  {
    id: "aj4-white-thunder",
    brand: "Jordan",
    model: "Air Jordan 4 Retro",
    colorway: "White Thunder",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-WHTTHUN",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj4-white-thunder.jpg",
  },
  {
    id: "aj4-og-sp-undefeated-2025",
    brand: "Jordan",
    model: "Air Jordan 4 Retro OG SP Undefeated",
    colorway: "Undefeated (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ4-UNDFTD25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj4-og-sp-undefeated-2025.jpg",
  },

  // ─── Jordan 5 (supplier drop) ─────────────────────────────────────────
  {
    id: "aj5-black-university-blue-2026",
    brand: "Jordan",
    model: "Air Jordan 5 Retro",
    colorway: "Black University Blue (2026)",
    priceCents: JORDAN_PRICE,
    sku: "AJ5-BUBLUE26",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj5-black-university-blue-2026.jpg",
  },
  {
    id: "aj5-fire-red-black-tongue-2025",
    brand: "Jordan",
    model: "Air Jordan 5 Retro",
    colorway: "Fire Red Black Tongue (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ5-FIRERED25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj5-fire-red-black-tongue-2025.jpg",
  },
  {
    id: "aj5-white-metallic-2026",
    brand: "Jordan",
    model: "Air Jordan 5 Retro",
    colorway: "White Metallic (2026)",
    priceCents: JORDAN_PRICE,
    sku: "AJ5-WHTMET26",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj5-white-metallic-2026.jpg",
  },
  {
    id: "aj5-tokyo-t23-2025",
    brand: "Jordan",
    model: "Air Jordan 5 Retro",
    colorway: "Tokyo T23 (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ5-TOKYO25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj5-tokyo-t23-2025.jpg",
  },
  {
    id: "aj5-og-black-metallic-reimagined",
    brand: "Jordan",
    model: "Air Jordan 5 Retro",
    colorway: "OG Black Metallic Reimagined",
    priceCents: JORDAN_PRICE,
    sku: "AJ5-OGBLKMET",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj5-og-black-metallic-reimagined.jpg",
  },
  {
    id: "aj5-medium-soft-pink",
    brand: "Jordan",
    model: "Air Jordan 5 Retro",
    colorway: "Medium Soft Pink",
    priceCents: JORDAN_PRICE,
    sku: "AJ5-SOFTPINK",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj5-medium-soft-pink.jpg",
  },

  // ─── Jordan 6 ─────────────────────────────────────────────────────────
  {
    id: "aj6-reverse-infrared",
    brand: "Jordan",
    model: "Air Jordan 6 Retro",
    colorway: "Reverse Infrared",
    priceCents: JORDAN_PRICE,
    sku: "AJ6-REVINFRA",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj6-reverse-infrared.jpg",
  },
  {
    id: "aj6-cap-and-gown",
    brand: "Jordan",
    model: "Air Jordan 6 Retro",
    colorway: "Cap and Gown",
    priceCents: JORDAN_PRICE,
    sku: "AJ6-CAPGOWN",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj6-cap-and-gown.jpg",
  },

  // ─── Jordan 11 (supplier drop) ────────────────────────────────────────
  {
    id: "aj11-dmp-defining-moments-2023",
    brand: "Jordan",
    model: "Air Jordan 11 Retro",
    colorway: "DMP Defining Moments (2023)",
    priceCents: JORDAN_PRICE,
    sku: "AJ11-DMP23",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj11-dmp-defining-moments-2023.jpg",
  },
  {
    id: "aj11-playoffs-2019",
    brand: "Jordan",
    model: "Air Jordan 11 Retro",
    colorway: "Playoffs (2019)",
    priceCents: JORDAN_PRICE,
    sku: "AJ11-PLYF19",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj11-playoffs-2019.jpg",
  },
  {
    id: "aj11-cool-grey-2021",
    brand: "Jordan",
    model: "Air Jordan 11 Retro",
    colorway: "Cool Grey (2021)",
    priceCents: JORDAN_PRICE,
    sku: "AJ11-COOLG21",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj11-cool-grey-2021.jpg",
  },
  {
    id: "aj11-cherry-2022",
    brand: "Jordan",
    model: "Air Jordan 11 Retro",
    colorway: "Cherry (2022)",
    priceCents: JORDAN_PRICE,
    sku: "AJ11-CHERRY22",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj11-cherry-2022.jpg",
  },
  {
    id: "aj11-space-jam-2016",
    brand: "Jordan",
    model: "Air Jordan 11 Retro",
    colorway: "Space Jam (2016)",
    priceCents: JORDAN_PRICE,
    sku: "AJ11-SPACEJ16",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj11-space-jam-2016.jpg",
  },

  // ─── Jordan 12 ────────────────────────────────────────────────────────
  {
    id: "aj12-taxi-2025",
    brand: "Jordan",
    model: "Air Jordan 12 Retro",
    colorway: "Taxi (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ12-TAXI25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj12-taxi-2025.jpg",
  },
  {
    id: "aj12-flu-game-2025",
    brand: "Jordan",
    model: "Air Jordan 12 Retro",
    colorway: "Flu Game (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ12-FLU25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj12-flu-game-2025.jpg",
  },
  {
    id: "aj12-french-blue-2025",
    brand: "Jordan",
    model: "Air Jordan 12 Retro",
    colorway: "French Blue (2025)",
    priceCents: JORDAN_PRICE,
    sku: "AJ12-FRBLUE25",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj12-french-blue-2025.jpg",
  },
  {
    id: "aj12-blueberry",
    brand: "Jordan",
    model: "Air Jordan 12 Retro",
    colorway: "Blueberry",
    priceCents: JORDAN_PRICE,
    sku: "AJ12-BLUEBRY",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj12-blueberry.jpg",
  },

  // ─── Jordan 13 ────────────────────────────────────────────────────────
  {
    id: "aj13-flint-2020",
    brand: "Jordan",
    model: "Air Jordan 13 Retro",
    colorway: "Flint (2020)",
    priceCents: JORDAN_PRICE,
    sku: "AJ13-FLINT20",
    sizesAvailable: SUPPLIER_SIZES_7_12,
    imageSrc: "/products/aj13-flint-2020.jpg",
  },
];

// ─── Shipping ─────────────────────────────────────────────────────────
export const SHIPPING_FLAT_CENTS = 999; // "Envío $9.99" per operator

// ─── Helpers ──────────────────────────────────────────────────────────
export function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toFixed(2)}`;
}

export function formatSizeRun(sizes: number[]): string {
  if (sizes.length === 0) return "Sold out";
  const min = Math.min(...sizes);
  const max = Math.max(...sizes);
  if (min === max) return `Size ${min}`;
  return `${min}–${max}`;
}

/** Pull the featured drop for the homepage hero. Highest-profile new release. */
export function getFeaturedProduct(): Product {
  const featured = PRODUCTS.find((p) => p.id === "aj4-toro-bravo-2026");
  if (!featured) throw new Error("Featured product missing from PRODUCTS");
  return featured;
}
